import { format, sub } from '@sequencehq/utils/dates'
import { billingScheduleAdapter } from 'modules/Cube/communication/external/billingSchedule.api.v1/adapters/billingSchedule.adapters'
import { LoadedBillingScheduleData } from 'modules/Cube/communication/external/billingSchedule.api.v1/ports/useLoadBillingScheduleEditor'
import { CubeReducerState } from 'modules/Cube/domain/cube.domain.types'

/**
 * An open-ended schedule has no end to append after, so the renewal instead
 * anchors at a chosen date and truncates the open phase there. To validate that
 * with the same proration logic as a fixed-end schedule, close the open final
 * phase the day before the renewal date so the appended phases begin exactly on
 * it and the billing-boundary alignment can be checked. A no-op for a fixed-end
 * schedule (its phases already have end dates) or when no renewal date is set.
 */
const closeOpenEndedFinalPhase = (
  loadedSchedule: LoadedBillingScheduleData,
  renewalStartDate: Date | undefined
): LoadedBillingScheduleData => {
  if (!renewalStartDate) {
    return loadedSchedule
  }
  const { phases } = loadedSchedule.billingSchedule
  const openFinalPhase = [...phases]
    .filter(phase => !phase.endDate)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .at(-1)
  if (!openFinalPhase) {
    return loadedSchedule
  }
  const dayBeforeRenewal = format(
    sub(renewalStartDate, { days: 1 }),
    'yyyy-MM-dd'
  )
  // When the renewal begins on (or before) the day the open phase starts, that
  // phase is fully superseded. Drop it rather than close it to the day before
  // its own start, which would be an invalid (end before start) window that
  // wrongly fails validation. If it is the only phase, leave it open instead —
  // the adapter can't represent a schedule with no phases, and a renewal at the
  // sole phase's start has no earlier boundary to misalign anyway.
  if (dayBeforeRenewal < openFinalPhase.startDate) {
    const remainingPhases = phases.filter(
      phase => phase.id !== openFinalPhase.id
    )
    if (remainingPhases.length === 0) {
      return loadedSchedule
    }
    return {
      ...loadedSchedule,
      billingSchedule: {
        ...loadedSchedule.billingSchedule,
        phases: remainingPhases
      }
    }
  }

  return {
    ...loadedSchedule,
    billingSchedule: {
      ...loadedSchedule.billingSchedule,
      phases: phases.map(phase =>
        phase.id === openFinalPhase.id
          ? { ...phase, endDate: dayBeforeRenewal }
          : phase
      )
    }
  }
}

/**
 * Produce the cube data for an existing schedule with a renewal quote's phases
 * appended to the end.
 *
 * Cube phases store durations, not absolute dates: the query layer chains them
 * from `common.startDate`. So appending the quote's phases to the schedule's
 * phase list makes them continue from the schedule's end date automatically —
 * no date arithmetic required here. The schedule's own settings (start date,
 * recurrence, tax, customer) are preserved as the anchor.
 */
export const buildCombinedScheduleData = (params: {
  loadedSchedule: LoadedBillingScheduleData
  quoteData: CubeReducerState['data']
  configuration: CubeReducerState['configuration']
  /**
   * When true, the first appended (quote) phase resets its billing period so it
   * bills from the renewal date rather than continuing the existing schedule's
   * period. This is what suppresses the proration errors that would otherwise
   * make the combined schedule invalid — see `getInvalidPriceProration`.
   */
  resetBillingPeriod?: boolean
  /**
   * Where the appended phases begin. Used to close an open-ended schedule's
   * final phase so it can be validated by the standard proration check.
   */
  renewalStartDate?: Date
}): Pick<CubeReducerState, 'data' | 'configuration'> & {
  existingPhaseIds: string[]
} => {
  const scheduleCube = billingScheduleAdapter.in(
    closeOpenEndedFinalPhase(params.loadedSchedule, params.renewalStartDate),
    params.configuration
  )

  const firstQuotePhaseId = params.quoteData.common.phaseIds[0]
  const quotePhases =
    params.resetBillingPeriod && firstQuotePhaseId
      ? {
          ...params.quoteData.phases,
          [firstQuotePhaseId]: {
            ...params.quoteData.phases[firstQuotePhaseId],
            recurrencePreference: 'RESET' as const
          }
        }
      : params.quoteData.phases

  const combinedData: CubeReducerState['data'] = {
    ...scheduleCube.data,
    phases: { ...scheduleCube.data.phases, ...quotePhases },
    prices: { ...scheduleCube.data.prices, ...params.quoteData.prices },
    discounts: {
      ...scheduleCube.data.discounts,
      ...params.quoteData.discounts
    },
    minimums: { ...scheduleCube.data.minimums, ...params.quoteData.minimums },
    products: { ...scheduleCube.data.products, ...params.quoteData.products },
    common: {
      ...scheduleCube.data.common,
      phaseIds: [
        ...scheduleCube.data.common.phaseIds,
        ...params.quoteData.common.phaseIds
      ]
    }
  }

  return {
    data: combinedData,
    configuration: scheduleCube.configuration,
    existingPhaseIds: scheduleCube.data.common.phaseIds
  }
}
